import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { request } from '../api/client';
import { useDebounce } from './useDebounce';

type Id = string | number;

export interface RecordSearchOptions {
  /** Query-key namespace for this picker. */
  key: string;
  /** Server scope (territoryId, assignedSalesPersonId…); each scope keeps its own cache entry. */
  params?: Record<string, unknown>;
  enabled?: boolean;
  /** The chosen id(s) — always kept in `rows`, fetched by id when never seen. */
  selected?: Id | Id[] | null;
  pageSize?: number;
}

/**
 * A CUSTOMER PICKER SEARCHES THE SERVER.
 *
 * Pickers used to load the first 200 (or 500) records and filter them in the browser, so with 319
 * retailers and thousands of farmers on the books anyone past the cut-off could not be found — or acted
 * for (PAWAR BROTHERS, E2E 15 Sep). The typed text now goes to `<url>?search=`.
 *
 * Spread onto the Select: `filterOption={false}` and `onSearch`. Feed `options` from `rows`.
 * - `selected` ids stay in `rows` even when the current search does not return them; an id never seen
 *   (URL filter, preset, record being edited) is fetched from `<url>/:id` for its label.
 * - `rowFor(id)` resolves a chosen id to its row; `remember(row)` seeds a row obtained elsewhere.
 */
export function useRecordSearch(url: string, { key, params, enabled = true, selected, pageSize = 50 }: RecordSearchOptions) {
  const [term, setTerm] = useState('');
  const q = useDebounce(term, 250).trim();
  // Every row this picker has been handed, so a pick survives a later search that does not return it.
  const known = useRef(new Map<string, any>());
  const [tick, setTick] = useState(0);

  const scope = JSON.stringify(params ?? {});
  const { data, isFetching } = useQuery({
    queryKey: [key, scope, q, pageSize],
    queryFn: () => request<any>({ url, params: { ...params, pageSize, ...(q ? { search: q } : {}) } }),
    enabled,
    // Keep the previous results while a new search loads — but never another scope's.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey?.[1] === scope ? prev : undefined),
  });
  const page: any[] = useMemo(() => (Array.isArray(data) ? data : (data?.data ?? [])), [data]);
  for (const r of page) known.current.set(String(r.id), r);

  const ids = (Array.isArray(selected) ? selected : selected == null ? [] : [selected])
    .map(String).filter((id) => id.trim() !== '');
  const idsKey = ids.join(',');
  const missing = enabled ? ids.filter((id) => !known.current.has(id)) : [];
  const fetched = useQueries({
    queries: missing.map((id) => ({
      queryKey: ['record-by-id', url, id],
      queryFn: () => request<any>({ url: `${url}/${id}` }),
    })),
  });
  for (const f of fetched) if (f.data?.id != null) known.current.set(String(f.data.id), f.data);
  const fetchedKey = fetched.map((f) => f.dataUpdatedAt).join(',');

  const rows: any[] = useMemo(() => {
    const onPage = new Set(page.map((r) => String(r.id)));
    const extra = idsKey ? idsKey.split(',').filter((id) => !onPage.has(id)).map((id) => known.current.get(id)).filter(Boolean) : [];
    return extra.length ? [...extra, ...page] : page;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, idsKey, fetchedKey, tick]);

  const rowFor = useCallback((id?: Id | null) => {
    if (id == null || String(id).trim() === '') return undefined;
    return page.find((r) => String(r.id) === String(id)) ?? known.current.get(String(id));
  }, [page]);

  const remember = useCallback((row: any) => {
    if (row?.id == null) return;
    known.current.set(String(row.id), row);
    setTick((t) => t + 1);
  }, []);

  return { rows, onSearch: setTerm, remember, rowFor, isFetching };
}

/** Retailer picker — `/retailers?search=` matches name, contact, mobile, code, town, territory, state. */
export const useRetailerSearch = (opts: RecordSearchOptions) => useRecordSearch('/retailers', opts);

/** Farmer picker — `/farmers?search=` matches name, mobile, WhatsApp, village, territory/district, state. */
export const useFarmerSearch = (opts: RecordSearchOptions) => useRecordSearch('/farmers', opts);
